import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { MessageSquare, Send, Trash2, Reply, User } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function CommentSection({ articleId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [total, setTotal] = useState(0);

  const load = () => {
    api.get(`/comments/${articleId}`).then(r => {
      setComments(r.data.comments || []);
      setTotal(r.data.total || 0);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, [articleId]);

  const submit = async (parentId = null) => {
    const content = parentId ? replyText : text;
    if (!content.trim()) return;
    if (!user) { toast.error("Please login to comment"); return; }
    setSending(true);
    try {
      await api.post("/comments", { article_id: articleId, text: content.trim(), parent_id: parentId });
      toast.success("Comment added");
      if (parentId) { setReplyText(""); setReplyTo(null); }
      else setText("");
      load();
    } catch { toast.error("Failed to add comment"); }
    setSending(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      toast.success("Comment deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const topLevel = comments.filter(c => !c.parent_id);
  const replies = {};
  comments.filter(c => c.parent_id).forEach(c => {
    if (!replies[c.parent_id]) replies[c.parent_id] = [];
    replies[c.parent_id].push(c);
  });

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div data-testid="comment-section">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="h-5 w-5 text-[#1E88E5]" />
        <h3 className="text-2xl font-serif font-bold text-foreground">Comments</h3>
        {total > 0 && <Badge variant="outline" className="text-xs font-sans">{total}</Badge>}
      </div>

      {/* Comment input */}
      {user ? (
        <div className="mb-8" data-testid="comment-form">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-[#1E88E5]/10 flex items-center justify-center rounded-full shrink-0">
              <User className="h-4 w-4 text-[#1E88E5]" />
            </div>
            <div className="flex-1">
              <Textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Share your thoughts on this article..."
                rows={3}
                className="font-sans text-sm"
                data-testid="comment-input"
              />
              <div className="flex justify-end mt-2">
                <Button
                  onClick={() => submit()}
                  disabled={sending || !text.trim()}
                  size="sm"
                  className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans"
                  data-testid="comment-submit"
                >
                  <Send className="h-3 w-3 mr-1.5" /> {sending ? "Posting..." : "Post Comment"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8 text-center border border-slate-200 dark:border-slate-700 p-6 bg-slate-50 dark:bg-slate-800/50">
          <p className="text-sm font-sans text-muted-foreground">
            <a href="/login" className="text-[#1E88E5] hover:underline">Login</a> to join the discussion
          </p>
        </div>
      )}

      {/* Comments list */}
      <div className="space-y-0">
        {topLevel.length === 0 && (
          <p className="text-sm font-sans text-muted-foreground text-center py-6">No comments yet. Be the first to share your thoughts.</p>
        )}
        {topLevel.map(comment => (
          <div key={comment.id} data-testid={`comment-${comment.id}`}>
            <div className="py-5 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700 flex items-center justify-center rounded-full shrink-0 text-xs font-sans font-bold text-slate-500">
                  {(comment.user_name || "A")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-sans font-medium text-sm text-foreground">{comment.user_name}</span>
                    <span className="text-xs font-sans text-muted-foreground">{timeAgo(comment.created_at)}</span>
                  </div>
                  <p className="text-sm font-sans text-slate-700 dark:text-slate-300 mt-1.5 leading-relaxed whitespace-pre-wrap">{comment.text}</p>
                  <div className="flex items-center gap-3 mt-2">
                    {user && (
                      <button
                        onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                        className="text-xs font-sans text-muted-foreground hover:text-[#1E88E5] transition-colors flex items-center gap-1"
                        data-testid={`reply-btn-${comment.id}`}
                      >
                        <Reply className="h-3 w-3" /> Reply
                      </button>
                    )}
                    {user && (user._id === comment.user_id || user.role === "admin") && (
                      <button
                        onClick={() => handleDelete(comment.id)}
                        className="text-xs font-sans text-muted-foreground hover:text-red-500 transition-colors flex items-center gap-1"
                        data-testid={`delete-comment-${comment.id}`}
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Reply form */}
              {replyTo === comment.id && (
                <div className="ml-11 mt-3">
                  <Textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder={`Reply to ${comment.user_name}...`}
                    rows={2}
                    className="font-sans text-sm"
                    data-testid={`reply-input-${comment.id}`}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <Button variant="ghost" size="sm" className="text-xs font-sans" onClick={() => { setReplyTo(null); setReplyText(""); }}>Cancel</Button>
                    <Button size="sm" className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans text-xs" onClick={() => submit(comment.id)} disabled={sending || !replyText.trim()} data-testid={`reply-submit-${comment.id}`}>
                      <Send className="h-3 w-3 mr-1" /> Reply
                    </Button>
                  </div>
                </div>
              )}

              {/* Replies */}
              {(replies[comment.id] || []).map(reply => (
                <div key={reply.id} className="ml-11 mt-3 pl-4 border-l-2 border-slate-200 dark:border-slate-700" data-testid={`reply-${reply.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-sans font-medium text-sm text-foreground">{reply.user_name}</span>
                    <span className="text-xs font-sans text-muted-foreground">{timeAgo(reply.created_at)}</span>
                  </div>
                  <p className="text-sm font-sans text-slate-700 dark:text-slate-300 mt-1 whitespace-pre-wrap">{reply.text}</p>
                  {user && (user._id === reply.user_id || user.role === "admin") && (
                    <button onClick={() => handleDelete(reply.id)} className="text-[11px] font-sans text-muted-foreground hover:text-red-500 mt-1 flex items-center gap-1">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
